import { UUID } from 'crypto';
import { useInfiniteQuery, useQuery } from 'react-query';
import { useAuth } from '../auth/AuthProvider';
import { isDateIntervalWithinGivenDays } from '../utils/date';
import { InsightDashboard } from '../utils/interfaces';
import { Report } from './../pages/Report/interactiveReport/overwatch-2-types';
import { getRequestHeaders, vake_api_server_url } from './helpers';
import { downloadFile } from './reports';

type PropsGetVesselsQuery = {
    data: ActorModeVessel[];
    nextPage: number | undefined;
};
export const useGetVesselsQuery = (
    uuid: string,
    startTime: Date | null,
    endTime: Date | null,
    verifiedActive: boolean,
    darkActive: boolean,
    unCorrelatedActive: boolean,
    tags: string[],
    maxDays: number,
    tagFilterStrategy: string
) => {
    const { token } = useAuth();
    // By adding noMatchingLabelActive number of DB requests are reduced.
    // Even better will be to have separate query for each of them.
    const noMatchingLabelActive = !(verifiedActive || darkActive || unCorrelatedActive);
    return useInfiniteQuery<PropsGetVesselsQuery, Error>({
        queryKey: [
            'queryKeyUseGetVesselsQuery',
            uuid,
            startTime,
            endTime,
            verifiedActive || noMatchingLabelActive,
            darkActive || noMatchingLabelActive,
            unCorrelatedActive || noMatchingLabelActive,
            tags,
            tagFilterStrategy,
        ],
        queryFn: async ({ pageParam = 0 }) => {
            const limit = 10;
            const skip = pageParam * limit;
            let url = `${vake_api_server_url}/actor-mode/all/${uuid}?`;
            url += `limit=${limit}`;
            url += `&skip=${skip}`;
            url += startTime ? `&start=${startTime.toISOString()}` : '';
            url += endTime ? `&end=${endTime.toISOString()}` : '';
            url += `&verified_active=${verifiedActive}`;
            url += `&dark_active=${darkActive}`;
            url += `&un_correlated_active=${unCorrelatedActive}`;
            if (tags.length > 0) {
                url += `&tags=${tags.join(',')}`;
            }
            url += `&tag_filter_strategy=${tagFilterStrategy}`;
            const res = await fetch(url, {
                credentials: 'include',
                headers: getRequestHeaders(token),
            });
            if (!res.ok) {
                try {
                    const errorMessage = await res.json();
                    throw new Error(errorMessage.detail);
                } catch {
                    throw new Error(res.statusText);
                }
            }
            if (res.status === 204) {
                return { data: [], nextPage: undefined };
            }
            const data = await res.json();
            const hasNextPage = data.length === limit;
            return { data, nextPage: hasNextPage ? pageParam + 1 : undefined };
        },
        getNextPageParam: (lastPage) => lastPage.nextPage,
        enabled:
            startTime !== null &&
            endTime !== null &&
            startTime < endTime &&
            isDateIntervalWithinGivenDays(startTime, endTime, maxDays),
    });
};

type ReportInfoApiResponse = {
    uuid: UUID;
    description: string;
    startTime: string | undefined;
    endTime: string | undefined;
};
export const useGetReportInfo = (uuid?: string) => {
    const { token } = useAuth();
    const url = `${vake_api_server_url}/report/overwatch/${uuid}/info`;

    return useQuery<Report>(
        ['queryKeyUseGetReportInfo', uuid],
        async () => {
            const res = await fetch(url, {
                credentials: 'include',
                headers: getRequestHeaders(token),
            });
            if (!res.ok) {
                throw new Error(res.statusText);
            }
            const apiData: ReportInfoApiResponse = await res.json();
            return {
                uuid: apiData.uuid,
                name: apiData.description,
                subscriptionStart: apiData.startTime ? new Date(apiData.startTime) : undefined,
                subscriptionEnd: apiData.endTime ? new Date(apiData.endTime) : undefined,
            };
        },
        {
            enabled: !!uuid,
        }
    );
};

export const useGetVesselQuery = (insightUuid?: string) => {
    const { token } = useAuth();
    const url = `${vake_api_server_url}/actor-mode/vessel/${insightUuid}`;
    return useQuery<ActorModeVessel>(
        ['queryKeyUseGetVesselQuery', insightUuid],
        async () => {
            const res = await fetch(url, {
                credentials: 'include',
                headers: getRequestHeaders(token),
            });
            if (!res.ok) {
                throw new Error(res.statusText);
            }
            return await res.json();
        },
        {
            enabled: !!insightUuid,
        }
    );
};

export const useGetVesselTagsQuery = (insightUuid?: string) => {
    const { token } = useAuth();
    const url = `${vake_api_server_url}/actor-mode/vessel/${insightUuid}/actions`;
    return useQuery<string[]>(
        ['queryKeyUseGetVesselTagsQuery', insightUuid],
        async () => {
            const res = await fetch(url, {
                credentials: 'include',
                headers: getRequestHeaders(token),
            });
            if (!res.ok) {
                throw new Error(res.statusText);
            }
            return await res.json();
        },
        { enabled: !!insightUuid }
    );
};

type PathfinderApiResponse = {
    url: string;
    name: string;
    uuid: string;
    startTime: string | null;
    endTime: string | null;
}[];
export const useGetVesselPathfinderQuery = (mmsi?: number) => {
    const { token } = useAuth();
    const url = `${vake_api_server_url}/actor-mode/vessel/${mmsi}/pathfinder`;
    return useQuery<PathfinderSubscriptionInfo[]>(
        ['queryKeyUseGetVesselPathfinderQuery', mmsi],
        async () => {
            const res = await fetch(url, {
                credentials: 'include',
                headers: getRequestHeaders(token),
            });
            if (!res.ok) {
                throw new Error(res.statusText);
            }
            const apiData: PathfinderApiResponse = await res.json();
            return apiData.map((item) => ({
                url: item.url,
                name: item.name,
                uuid: item.uuid,
                startTime: item.startTime ? new Date(item.startTime) : null,
                endTime: item.endTime ? new Date(item.endTime) : null,
            }));
        },
        { enabled: mmsi !== undefined }
    );
};

const queryKeyUseGetInsights = (mmsi: number | null, inside: boolean, uuid: string, insightUuid: string) => [
    'queryKeyUseGetInsights',
    mmsi,
    inside,
    uuid,
    insightUuid,
];
export const useGetInsights = (
    mmsi: number | null,
    enabled: boolean,
    inside: boolean,
    uuid: string,
    insightUuid: string
) => {
    const { token } = useAuth();
    let url = `${vake_api_server_url}/actor-mode/something?inside=${inside}&uuid=${uuid}&insight_uuid=${insightUuid}`;
    if (mmsi !== null) {
        url = `${url}&mmsi=${mmsi}`;
    }
    return useQuery<InsightDashboard[]>(
        queryKeyUseGetInsights(mmsi, inside, uuid, insightUuid),
        async () => {
            const res = await fetch(url, {
                credentials: 'include',
                headers: getRequestHeaders(token),
            });
            if (!res.ok) {
                throw new Error(res.statusText);
            }
            return await res.json();
        },
        { enabled: enabled && (mmsi !== null || !!insightUuid) }
    );
};

const queryKeyUseGetNewestInsight = (insightUuid: string) => ['queryKeyUseGetNewestInsight', insightUuid];
export const useGetNewestInsight = (insightUuid: string) => {
    const { token } = useAuth();
    const url = `${vake_api_server_url}/actor-mode/vessel/${insightUuid}/newest-insight`;
    return useQuery<InsightDashboard>(
        queryKeyUseGetNewestInsight(insightUuid),
        async () => {
            const res = await fetch(url, {
                credentials: 'include',
                headers: getRequestHeaders(token),
            });
            if (!res.ok) {
                throw new Error(res.statusText);
            }
            return await res.json();
        },
        { enabled: insightUuid.length > 0 }
    );
};

export const useDownload = (
    insightUuid: string,
    download: boolean,
    startTime: Date | null,
    endTime: Date | null,
    verifiedActive: boolean,
    darkActive: boolean,
    unCorrelatedActive: boolean,
    tags: string[],
    maxDays: number,
    tagFilterStrategy: string
) => {
    const { token } = useAuth();
    // By adding noMatchingLabelActive number of DB requests are reduced.
    // Even better will be to have separate query for each of them.
    const noMatchingLabelActive = !(verifiedActive || darkActive || unCorrelatedActive);
    let url = `${vake_api_server_url}/actor-mode/download/${insightUuid}?`;
    url += startTime ? `&start=${startTime.toISOString()}` : '';
    url += endTime ? `&end=${endTime.toISOString()}` : '';
    url += `&verified_active=${verifiedActive}`;
    url += `&dark_active=${darkActive}`;
    url += `&un_correlated_active=${unCorrelatedActive}`;
    if (tags.length > 0) {
        url += `&tags=${tags.join(',')}`;
    }
    url += `&tag_filter_strategy=${tagFilterStrategy}`;
    return useQuery<Blob, Error>(
        [
            'queryKeyUseDownloadFullDataset',
            insightUuid,
            startTime,
            endTime,
            verifiedActive || noMatchingLabelActive,
            darkActive || noMatchingLabelActive,
            unCorrelatedActive || noMatchingLabelActive,
            tags,
            tagFilterStrategy,
        ],
        async () => downloadFile(url, token || ''),
        {
            enabled:
                download &&
                startTime !== null &&
                endTime !== null &&
                startTime < endTime &&
                isDateIntervalWithinGivenDays(startTime, endTime, maxDays),
        }
    );
};
